/**
 * Raggruppamenti custom di mansioni per la rendicontazione costi personale
 * (es. "Settore medico" = Infermiere+OSS) — una mansione può stare in più
 * gruppi contemporaneamente, non è una partizione. Usati dal report
 * costo-personale (groupBy=gruppoCosto) e dal confronto tra RSA.
 */
import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  Spinner,
  ErrorBox,
  SimpleTable,
  Badge,
} from "../../components/ui/basics";

const FORM_INIT = { codice: "", nome: "", mansioni: [], note: "" };

export default function GruppiMansioneCostoSection() {
  const { apiRequest } = useAuth();
  const { data, loading, error, reload } = useApi(
    "/costo-personale/gruppi?tutti=1",
  );
  const { data: catalogo } = useApi("/planning/mansioni");
  const [form, setForm] = useState(FORM_INIT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const mansioniDisponibili = catalogo?.items || [];

  const toggleMansione = (codice) => {
    setForm((f) => ({
      ...f,
      mansioni: f.mansioni.includes(codice)
        ? f.mansioni.filter((m) => m !== codice)
        : [...f.mansioni, codice],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await apiRequest("/costo-personale/gruppi", {
        method: "POST",
        body: JSON.stringify({
          codice: form.codice.trim().toUpperCase(),
          nome: form.nome.trim(),
          mansioni: form.mansioni,
          note: form.note.trim() || null,
        }),
      });
      setForm(FORM_INIT);
      reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleAttivo = async (r) => {
    await apiRequest(`/costo-personale/gruppi/${r.id}`, {
      method: "PATCH",
      body: JSON.stringify({ attivo: !r.attivo }),
    });
    reload();
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare il gruppo "${r.nome}"?`)) return;
    await apiRequest(`/costo-personale/gruppi/${r.id}`, { method: "DELETE" });
    reload();
  };

  const nomeMansione = (codice) =>
    mansioniDisponibili.find((m) => m.codice === codice)?.nome || codice;

  return (
    <Card
      title="Gruppi mansioni (costo personale)"
      subtitle="Raggruppamenti custom per il report di rendicontazione — una mansione può appartenere a più gruppi (es. 'Settore medico' = Infermiere + OSS)."
    >
      {data?.source === "default" && (
        <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mb-3">
          ⚠ Entità non ancora creata sul tenant Dataverse (gate schema, richiede
          approvazione). Il catalogo gruppi è vuoto finché non viene eseguito lo
          script di setup.
        </p>
      )}
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}

      {data && data.items.length > 0 && (
        <SimpleTable
          rowKey={(r) => r.id}
          columns={[
            { key: "codice", label: "Codice" },
            { key: "nome", label: "Nome" },
            {
              key: "mansioni",
              label: "Mansioni incluse",
              render: (r) => (
                <div className="flex flex-wrap gap-1">
                  {r.mansioni.map((m) => (
                    <span
                      key={m}
                      className="text-xs bg-gray-100 rounded px-1.5 py-0.5"
                    >
                      {nomeMansione(m)}
                    </span>
                  ))}
                </div>
              ),
            },
            {
              key: "attivo",
              label: "Stato",
              render: (r) => (
                <button onClick={() => toggleAttivo(r)}>
                  <Badge status={r.attivo ? "ok" : "default"}>
                    {r.attivo ? "Attivo" : "Disattivo"}
                  </Badge>
                </button>
              ),
            },
            {
              key: "azioni",
              label: "",
              render: (r) => (
                <button
                  onClick={() => elimina(r)}
                  className="text-red-600 hover:underline text-xs"
                >
                  Elimina
                </button>
              ),
            },
          ]}
          rows={data.items}
        />
      )}

      <form onSubmit={submit} className="mt-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <span className="block text-xs text-gray-500 mb-1">Codice</span>
            <input
              type="text"
              value={form.codice}
              onChange={(e) => setForm({ ...form, codice: e.target.value })}
              required
              placeholder="MEDICO"
              className="border border-gray-300 rounded px-2 py-1.5 w-32"
            />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-gray-500 mb-1">Nome</span>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              required
              placeholder="Settore medico"
              className="border border-gray-300 rounded px-2 py-1.5 w-56"
            />
          </label>
          <label className="text-sm flex-1 min-w-[16rem]">
            <span className="block text-xs text-gray-500 mb-1">Note</span>
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="border border-gray-300 rounded px-2 py-1.5 w-full"
            />
          </label>
        </div>

        <div>
          <span className="block text-xs text-gray-500 mb-1">
            Mansioni incluse ({form.mansioni.length} selezionate)
          </span>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded p-2">
            {mansioniDisponibili.map((m) => (
              <label
                key={m.codice}
                className="flex items-center gap-1 text-xs bg-gray-50 rounded px-2 py-1 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={form.mansioni.includes(m.codice)}
                  onChange={() => toggleMansione(m.codice)}
                />
                {m.nome}
              </label>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving || form.mansioni.length === 0}
          className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? "Salvataggio…" : "+ Nuovo gruppo"}
        </button>
      </form>
      {formError && <p className="text-xs text-red-600 mt-2">{formError}</p>}
    </Card>
  );
}
